import { Component, EventEmitter, Input, OnInit, Output, SimpleChanges, ViewChild } from '@angular/core';
import { BehaviorSubject, firstValueFrom, Subject, Subscription } from 'rxjs';
import { CustomModalComponent } from 'src/app/components/custom-modal/custom-modal.component';
import { InventoryEntryStateEnums } from 'src/app/enum/inventory-entry-enums';
import { OptionWindow } from 'src/app/enum/option-window-enums';
import { ProfileUserEnums } from 'src/app/enum/profile-user-enums';
import { ActionColor, CheckBoxAction, TableAction } from 'src/app/interfaces/custom-table/custom-table-action.interface';
import { OptionWindowModel } from 'src/app/models/authentication/option-window.model';
import { UserModel } from 'src/app/models/authentication/user.model';
import { InventoryEntryDetailModel } from 'src/app/models/inventory/inventory-entry-detail.model';
import { InventoryEntryModel } from 'src/app/models/inventory/inventory-entry.model';
import { ColumnResponseModel } from 'src/app/models/responses/column-response.model';
import { PagedResponseModel } from 'src/app/models/responses/paged-response.model';
import { InventoryEntryDetailCaptureTableModel } from 'src/app/models/tables/inventory/inventory-entry-detail-capture-table.model';
import { InventoryEntryDetailTableModel } from 'src/app/models/tables/inventory/inventory-entry-detail-table.model';
import { ConfirmDialogService } from 'src/app/services/confirm-dialog/confirm-dialog.service';
import { InventoryEntryDetailService } from 'src/app/services/inventory/inventory-entry-detail.service';
import { ToastHandlerService } from 'src/app/services/toast/toast-handler.service';
import { ImportInventoryEntryComponent } from '../../inventory-entry/import-inventory-entry/import-inventory-entry.component';

@Component({
  selector: 'app-inventory-entry-detail',
  templateUrl: './inventory-entry-detail.component.html',
  styleUrls: ['./inventory-entry-detail.component.scss'],
  standalone: false
})
export class InventoryEntryDetailComponent implements OnInit {

  @Input() inventoryEntryModel: InventoryEntryModel = new InventoryEntryModel();
  @Input() quantityDetails: number = 0;
  @Input() disabledDetail: boolean = false;
  @Input() disabledActions: boolean = false;
  @Input() userAuthenticated: UserModel = new UserModel();
  @Input() permissionsUser: OptionWindowModel[] = [];
  @Input() permissionUpdatable: boolean = false;
  @Output() emitQuantityDetails = new EventEmitter<number>();

  @ViewChild('importDetail') importDetailModalComponent?: CustomModalComponent | null = null;

  ascendant: boolean = true;
  columnSort: string | null = null;
  checkboxActions: CheckBoxAction[] = [
    {
      label: 'Eliminar',
      icon: 'pi pi-trash',
      color: ActionColor.DANGER,
      action: 'bulk-delete'
    },
  ];
  clearSelectionTrigger: number = 0;
  componentImport = ImportInventoryEntryComponent;
  detailData: PagedResponseModel<InventoryEntryDetailCaptureTableModel> =
    new PagedResponseModel<InventoryEntryDetailCaptureTableModel>();
  detailHeader: ColumnResponseModel[] = [];
  disabledButton: boolean = true;
  disabledImportButton: boolean = true;
  isFormView: boolean = false;
  inputsForImport$ = new BehaviorSubject<any>({
    inventoryEntryModel: new InventoryEntryModel()
  })
  inventoryEntryTypeIdColumns: number = 0;
  limit: number = 10;
  inventoryEntryDetail: InventoryEntryDetailModel = new InventoryEntryDetailModel();
  outputForImport$ = new Subject<any>();
  page: number = 1;
  subscriptionForImport: Subscription;
  tableActions: TableAction[] = [];

  constructor(private confirmDialogService: ConfirmDialogService,
    private inventoryEntryDetailService: InventoryEntryDetailService,
    private toastHandlerService: ToastHandlerService) { }

  async ngOnInit() {
    await this.getColumns();
    this.tableActions = this.getDefaultActions();

    this.subscriptionForImport = this.outputForImport$.subscribe(({ type }) => {
      switch (type) {
        case 'onReload':
          this.getInventoryEntryDetailsByInventoryEntry();
          break;
        default:
          console.warn('Tipo de evento desconocido:', type);
      }
    });
  }

  ngOnChanges(changes: SimpleChanges) {
    const inventoryEntryModel = changes["inventoryEntryModel"];
    if (inventoryEntryModel && !inventoryEntryModel.firstChange) {
      //Las columnas cambian según el tipo de entrada (la devolución muestra la entrada de origen):
      if (this.inventoryEntryModel.inventoryEntryTypeId !== this.inventoryEntryTypeIdColumns) {
        this.getColumns();
      }

      this.getFormView(false);
      this.getInventoryEntryDetailsByInventoryEntry();
      this.inventoryEntryDetail = new InventoryEntryDetailModel();

      const isNewOrClosed =
        this.inventoryEntryModel.inventoryEntryId === 0 ||
        this.inventoryEntryModel.inventoryEntryStateId !== InventoryEntryStateEnums.open;

      this.disabledButton = isNewOrClosed;
      this.disabledImportButton = isNewOrClosed || this.inventoryEntryModel.swRequirePurchaseOrder;

      if (!this.disabledImportButton) this.updateInputs();
      this.clearSelectionTrigger++;
    }

    const disabledActions = changes["disabledActions"];
    if (disabledActions && !disabledActions.firstChange) {
      this.tableActions = this.disabledActions ? [] : this.getDefaultActions();
    }
  }

  async clicDetail() {
    if (this.inventoryEntryModel.inventoryEntryId != 0) {
      document.getElementById("btn-select-detail")?.click();
    } else {
      this.toastHandlerService.showToast(
        'warning',
        `Debe crear el encabezado del detalle.`
      );
    }
  }

  async createNewDetail() {
    this.inventoryEntryDetail = new InventoryEntryDetailModel();
    this.getFormView(true);
  }

  async deleteInventoryEntryDetail(selectedIds: number[]) {
    if (selectedIds.length === 0) return;

    const confirmed = await this.confirmDialogService.confirm(
      '¿Realmente desea eliminar el detalle de la entrada de inventario?'
    );
    if (confirmed) {
      try {
        await firstValueFrom(this.inventoryEntryDetailService.deleteSomeDetails(selectedIds));
        this.toastHandlerService.showToast(
          'success',
          `El detalle de la entrada de inventario fue eliminado exitosamente.`
        );
        this.clearSelectionTrigger++;
        this.getUpdateListDetailByDelete();
      } catch (error) {
        console.error('Error al eliminar el detalle de la entrada de inventario.', error);
      }
    }
  }

  async getColumns() {
    try {
      this.inventoryEntryTypeIdColumns = this.inventoryEntryModel.inventoryEntryTypeId;
      this.detailHeader = await firstValueFrom(
        this.inventoryEntryDetailService.getColumnsForCaptureTable(this.inventoryEntryTypeIdColumns)
      );
    } catch (error) {
      console.error('Error al buscar las columnas para la consulta.', error);
    }
  }

  async getInventoryEntryDetailsByInventoryEntry() {
    try {
      this.detailData = await firstValueFrom(
        this.inventoryEntryDetailService.getInventoryEntryDetailsForCaptureTable(
          this.page, this.limit, this.columnSort, this.ascendant, this.inventoryEntryModel.inventoryEntryId
        )
      );
      this.emitQuantityDetails.emit(this.detailData.totalItems);
    } catch (error) {
      console.error('Error al buscar los detalles de la entrada de inventario.', error);
    }
  }

  getDefaultActions(): TableAction[] {
    return [
      {
        label: 'Editar',
        icon: 'pi pi-pencil',
        action: 'edit'
      }
    ];
  }

  async getUpdateListDetailByDelete() {
    this.inventoryEntryDetail = new InventoryEntryDetailModel();
    this.clearSelectionTrigger++;
    await this.getInventoryEntryDetailsByInventoryEntry();
  }

  getFormView(isFormView: boolean) {
    this.isFormView = isFormView;
  }

  async handleSelectedItem() {
    await this.getInventoryEntryDetailsByInventoryEntry();
  }

  async onAction(event: { action: string, item?: InventoryEntryDetailTableModel, selectedItems?: number[] }) {
    if (event.action === 'edit') {
      const response = await firstValueFrom(this.inventoryEntryDetailService.getInventoryEntryDetailById(event.item!.id));
      if (response)
        this.inventoryEntryDetail = response;
      this.getFormView(true);
    }
    if (event.action === 'bulk-delete' && event.selectedItems) {
      await this.deleteInventoryEntryDetail(event.selectedItems);
    }
  }

  async onPageChange(event: { page: number; pageSize: number, sortField: string | undefined, sortOrder: number }) {
    this.page = event.page;
    this.limit = event.pageSize;
    this.columnSort = event.sortField || null;
    this.ascendant = event.sortOrder === 1;
    await this.getInventoryEntryDetailsByInventoryEntry();
  }

  async openImportModal() {
    if (this.userAuthenticated.profileUser.code == ProfileUserEnums.administrador || this.permissionsUser.find(x => x.code == OptionWindow.Importar)) {
      if (this.permissionUpdatable) {
        if (this.detailData.totalItems > 0) {
          this.toastHandlerService.showToast(
            'warning',
            `La entrada ya contiene registros. Debe estar vacía para poder importar.`
          );
          return;
        }
        this.updateInputs();
        this.importDetailModalComponent && this.importDetailModalComponent.show();
      } else {
        this.toastHandlerService.showToast(
          'warning',
          `El usuario no tiene permisos para la edición de Entradas de Inventario. Comuníquese con el administrador del sistema.`
        );
      }
    } else {
      this.toastHandlerService.showToast(
        'warning',
        `El usuario no tiene permisos para importar Entradas de Inventario, comuniquese con el administrador del sistema.`
      );
    }
  }

  updateInputs() {
    this.inputsForImport$.next({
      inventoryEntryModel: this.inventoryEntryModel
    });
  }

}
